import { EventTracker } from '@/utils';
import { cn } from '@/utils/styles';
import { Card, CardContent } from '@design-system/Card';
import { Skeleton } from '@design-system/Skeleton';
import { TrackingSampleModal } from '@screens/HomePage/components/TrackingSampleModal';
import { useGetKitStatus } from '@viome/viome-base/src/usecase/Home/HomeQuery';
import { ChevronRight, CircleAlert } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

interface HomeTrackProgressProps {
  screen: 'nornr' | 'retest';
}

export const HomeTrackProgress = ({ screen }: HomeTrackProgressProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { data: testStatus, isLoading, isError } = useGetKitStatus();
  const [searchParams] = useSearchParams();

  const isRetestMode = screen === 'retest';
  const issueIncurrentStatus = testStatus?.is_issue_in_current_status ?? false;

  useEffect(() => {
    const shouldOpenModal = searchParams.get('trackMySample');
    if (shouldOpenModal === 'true') {
      setIsModalOpen(true);
      // window.history.replaceState(null, '', window.location.pathname);
    }
  }, [searchParams]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton
          className={cn('h-8 w-48', isRetestMode && 'rounded-lg bg-white/10')}
        />
        <Skeleton
          className={cn(
            'h-52 w-full',
            isRetestMode && 'rounded-lg bg-white/10',
          )}
        />
      </div>
    );
  }

  if (isError || !testStatus) {
    return;
  }

  const getProgressPercentage = () => {
    return (
      (testStatus.track_status_id / testStatus.track_status_list.length) * 100
    );
  };

  return (
    <>
      <div className="space-y-4">
        <h2
          data-testid="home-trackProgress-heading"
          className={cn(
            'typography-h2',
            isRetestMode ? 'text-[#C8CFDBCC]' : 'text-primary',
          )}
        >
          {testStatus?.title}
        </h2>

        <Card
          data-testid="home-trackProgress-card"
          className={cn(
            'relative  border-0',
            testStatus.track_status_id !== 0 && 'cursor-pointer',
            isRetestMode
              ? [
                  'bg-[#5B5C644D]',
                  'backdrop-blur-[50px]',
                  'shadow-[0px_1px_0px_0px_#FFFFFF0A_inset,0px_1px_3px_0px_#0000001A]',
                ]
              : 'bg-background-blur-bold',
          )}
          onClick={() => {
            if (testStatus.track_status_id !== 0) {
              EventTracker.trackEvent(
                'home',
                'sample_tracking_details_timeline',
              );
              return setIsModalOpen(true);
            }
          }}
        >
          <CardContent className="flex flex-col gap-4 p-6">
            <div className="flex items-start justify-between">
              <div className="flex flex-col gap-1">
                <span
                  data-testid="home-trackProgress-statusHeader"
                  className={cn(
                    'typography-h3 mb-4',
                    isRetestMode ? 'text-[#C8CFDBCC]' : 'text-primary',
                  )}
                >
                  {testStatus.status_header}
                </span>
                <span
                  data-testid="home-trackProgress-statusSubheader"
                  className={cn(
                    'typography-body mb-2',
                    isRetestMode
                      ? 'text-[#C8CFDB80]'
                      : 'text-tertiary opacity-80',
                  )}
                >
                  {testStatus.status_subheader}
                </span>
              </div>
              {testStatus.track_status_id !== 0 ? (
                <ChevronRight
                  className={cn(
                    'h-5 w-5',
                    isRetestMode ? 'text-[#C8CFDB80]' : 'text-secondary/80',
                  )}
                />
              ) : null}
            </div>

            <div data-testid="home-trackProgress-progressBarContainer" className="w-full h-1 bg-background-secondary rounded-full relative">
              <div
                data-testid="home-trackProgress-progressBar"
                className="h-full bg-background-success transition-all duration-300 ease-in-out"
                style={{
                  width: `${getProgressPercentage()}%`,
                }}
              />
              {issueIncurrentStatus && (
                <div
                  className="absolute top-1/2 -translate-y-1/2"
                  style={{
                    left: `${getProgressPercentage()}%`,
                  }}
                >
                  <CircleAlert stroke="white" fill="red" size={20} />
                </div>
              )}
            </div>

            <div className="flex justify-between">
              {testStatus.track_status_list.map((stage, index) => (
                <span
                  key={stage}
                  data-testid={`home-trackProgress-stage-${index}`}
                  className={cn(
                    'typography-caption2',
                    index + 1 === testStatus.track_status_id
                      ? isRetestMode
                        ? 'text-[#C8CFDBCC] font-medium'
                        : 'text-primary font-medium'
                      : isRetestMode
                        ? 'text-[#C8CFDB80]'
                        : 'text-secondary',
                  )}
                >
                  {stage}
                </span>
              ))}
            </div>

            {/* {issueIncurrentStatus && (
              <Button
                asChild
                className="[&_svg]:!size-6 mt-2 mb-1 flex items-center justify-center gap-2"
              >
                <a
                  href={linkoutConfigs.faqs}
                  target="_blank"
                  onClick={(e) => e.stopPropagation()}
                >
                  Contact Support
                </a>
              </Button>
            )} */}
          </CardContent>
        </Card>
      </div>

      <TrackingSampleModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </>
  );
};
